import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { clsx } from "clsx";
import { GripVertical, Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import type {
	CreateRdoScheduledServiceInput,
	UpdateRdoScheduledServiceInput,
} from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StageCombobox } from "@/features/stages/components/stage-combobox";
import type { Stage } from "@/features/stages/types.ts";
import type { Rdo, RdoScheduledService } from "../../types.ts";
import {
	isScheduledServiceLimitReached,
	MAX_RDO_SCHEDULED_SERVICES,
	RDO_SCHEDULED_SERVICE_MAX_LENGTH,
} from "../../utils/scheduled-services.ts";

interface ScheduledServicesSectionProps {
	rdo: Rdo;
	stages: Stage[];
	readOnly: boolean;
	creating: boolean;
	onCreate: (input: CreateRdoScheduledServiceInput) => Promise<unknown>;
	onUpdate: (serviceId: string, input: UpdateRdoScheduledServiceInput) => void;
	onDelete: (serviceId: string) => void;
	onReorder: (ids: string[]) => void;
}

export function ScheduledServicesSection({
	rdo,
	stages,
	readOnly,
	creating,
	onCreate,
	onUpdate,
	onDelete,
	onReorder,
}: ScheduledServicesSectionProps) {
	const [newDescription, setNewDescription] = useState("");
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);
	const services = rdo.scheduledServices;
	const ids = services.map((service) => service.id);
	const limitReached = isScheduledServiceLimitReached(services.length);

	function handleDragEnd(event: DragEndEvent) {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const from = ids.indexOf(String(active.id));
		const to = ids.indexOf(String(over.id));
		if (from < 0 || to < 0) return;
		onReorder(arrayMove(ids, from, to));
	}

	async function submitNew(event: React.FormEvent) {
		event.preventDefault();
		const description = newDescription.trim();
		if (!description || limitReached) return;
		await onCreate({ description });
		setNewDescription("");
	}

	return (
		<section
			className="rdo-editor__section"
			aria-labelledby="rdo-scheduled-services"
		>
			<div className="rdo-editor__section-header">
				<h2 id="rdo-scheduled-services" className="rdo-editor__section-title">
					Serviços programados
				</h2>
				<span className="rdo-editor__section-count">{services.length}</span>
			</div>
			<p className="rdo-editor__hint">
				Previstos para o próximo dia de trabalho.
			</p>

			{services.length === 0 ? (
				<p className="rdo-editor__empty">
					Nenhum serviço programado — a seção não aparece no PDF.
				</p>
			) : (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext items={ids} strategy={verticalListSortingStrategy}>
						<ol className="rdo-scheduled-services">
							{services.map((service, index) => (
								<ScheduledServiceItem
									key={service.id}
									service={service}
									index={index}
									stages={stages}
									readOnly={readOnly}
									onUpdate={(input) => onUpdate(service.id, input)}
									onDelete={() => onDelete(service.id)}
								/>
							))}
						</ol>
					</SortableContext>
				</DndContext>
			)}

			{!readOnly && (
				<form className="rdo-scheduled-services__new" onSubmit={submitNew}>
					<Textarea
						aria-label="Descrição do novo serviço"
						placeholder="Descreva o serviço previsto"
						className="rdo-scheduled-services__new-input"
						value={newDescription}
						maxLength={RDO_SCHEDULED_SERVICE_MAX_LENGTH}
						onChange={(event) => setNewDescription(event.target.value)}
						disabled={creating || limitReached}
					/>
					<div className="rdo-scheduled-services__new-actions">
						{limitReached ? (
							<span className="rdo-scheduled-services__limit">
								Limite de {MAX_RDO_SCHEDULED_SERVICES} serviços
							</span>
						) : (
							<span className="rdo-editor__counter">
								{newDescription.length}/{RDO_SCHEDULED_SERVICE_MAX_LENGTH}
							</span>
						)}
						<Button
							type="submit"
							disabled={creating || limitReached || !newDescription.trim()}
						>
							{creating ? <Loader2 /> : <Plus />}
							Adicionar serviço
						</Button>
					</div>
				</form>
			)}
		</section>
	);
}

interface ScheduledServiceItemProps {
	service: RdoScheduledService;
	index: number;
	stages: Stage[];
	readOnly: boolean;
	onUpdate: (input: UpdateRdoScheduledServiceInput) => void;
	onDelete: () => void;
}

function ScheduledServiceItem({
	service,
	index,
	stages,
	readOnly,
	onUpdate,
	onDelete,
}: ScheduledServiceItemProps) {
	const [description, setDescription] = useState(service.description);
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: service.id, disabled: readOnly });

	const position = index + 1;
	const stageName = stages.find((stage) => stage.id === service.stageId)?.name;
	const descriptionId = `rdo-scheduled-service-${service.id}-description`;
	const stageId = `rdo-scheduled-service-${service.id}-stage`;

	function commitDescription() {
		const next = description.trim();
		if (!next) {
			setDescription(service.description);
			return;
		}
		setDescription(next);
		if (next !== service.description) onUpdate({ description: next });
	}

	return (
		<li
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			className={clsx(
				"rdo-scheduled-services__item",
				isDragging && "is-dragging",
			)}
			data-testid="rdo-scheduled-service"
			aria-label={`Serviço programado ${position}`}
		>
			{!readOnly && (
				<button
					type="button"
					className="rdo-scheduled-services__handle"
					aria-label={`Reordenar serviço ${position}`}
					{...attributes}
					{...listeners}
				>
					<GripVertical />
				</button>
			)}
			<span className="rdo-scheduled-services__index">{position}</span>

			{readOnly ? (
				<div className="rdo-scheduled-services__body">
					<p className="rdo-scheduled-services__description">
						{service.description}
					</p>
					{stageName && (
						<p className="rdo-scheduled-services__stage">{stageName}</p>
					)}
				</div>
			) : (
				<div className="rdo-scheduled-services__body">
					<div className="rdo-scheduled-services__field">
						<Label htmlFor={descriptionId} className="visually-hidden">
							Descrição do serviço {position}
						</Label>
						<Textarea
							id={descriptionId}
							className="rdo-scheduled-services__textarea"
							value={description}
							maxLength={RDO_SCHEDULED_SERVICE_MAX_LENGTH}
							onChange={(event) => setDescription(event.target.value)}
							onBlur={commitDescription}
						/>
						<span className="rdo-editor__counter">
							{description.length}/{RDO_SCHEDULED_SERVICE_MAX_LENGTH}
						</span>
					</div>
					<div className="rdo-scheduled-services__field">
						<Label htmlFor={stageId}>Etapa</Label>
						<StageCombobox
							id={stageId}
							stages={stages}
							value={service.stageId ?? ""}
							onChange={(next) => {
								if ((next || undefined) !== service.stageId) {
									onUpdate({ stageId: next || null });
								}
							}}
							placeholder="Sem etapa"
							noneLabel="Sem etapa"
						/>
					</div>
				</div>
			)}

			{!readOnly && (
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="rdo-scheduled-services__remove"
					aria-label={`Remover serviço ${position}`}
					onClick={onDelete}
				>
					<Trash2 />
				</Button>
			)}
		</li>
	);
}
